/**
 * Oath Power Action Service
 * API calls for power actions (Set-Aside, Discharge) using the new Oath authentication
 * Set-Aside routes: /oath/set-aside/*
 * Discharge routes: /oath/powerinquiry/powers/*
 */

import { OathStorageKeys } from '../models/userModels';
import { oathRefreshToken } from './oathAuthService';

const BASE_URL = process.env.REACT_APP_BASE_URL;

/**
 * Get auth headers with JWT token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

/**
 * Authenticated fetch wrapper with auto-refresh on 401
 */
const authFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  if (response.status === 401) {
    const refreshResult = await oathRefreshToken();
    if (refreshResult.success) {
      return fetch(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...options.headers,
        },
      });
    }
  }

  return response;
};

/**
 * Parse API response - handles BaseResponse wrapper
 */
const parseResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  return data.result !== undefined ? data.result : data;
};

// ============================================
// CONSTANTS
// ============================================

/**
 * Fallback set-aside reason codes (used when API is unavailable)
 */
export const SET_ASIDE_REASON_CODES = [
  { code: 'BD', description: 'Bond Discharged' },
  { code: 'CA', description: 'Case Dismissed' },
  { code: 'CT', description: 'Court Error' },
  { code: 'OT', description: 'Other' },
];

// ============================================
// SET-ASIDE OPERATIONS
// Base route: /oath/set-aside
// ============================================

/**
 * Fetch set-aside reason codes from the API
 * Falls back to hardcoded SET_ASIDE_REASON_CODES on failure
 * @returns {Promise<{success: boolean, data?: Array<{code: string, description: string}>, error?: string}>}
 */
export const getSetAsideReasonCodes = async () => {
  try {
    const response = await authFetch(`${BASE_URL}oath/set-aside/reason-codes`);
    const json = await response.json().catch(() => ({}));

    if (!response.ok || !json.success) {
      return { success: false, error: json.message || 'Failed to fetch reason codes', data: SET_ASIDE_REASON_CODES };
    }

    return { success: true, data: json.data || SET_ASIDE_REASON_CODES };
  } catch (error) {
    console.error('Fetch set-aside reason codes error:', error);
    return { success: false, error: 'Network error', data: SET_ASIDE_REASON_CODES };
  }
};

/**
 * Validate whether a power can be set aside.
 * Call this before showing the set-aside form.
 * @param {string} powerNumber - The power number (e.g., "A1234567")
 * @returns {Promise<{success: boolean, canSetAside?: boolean, message?: string, powerNumber?: string, defendantName?: string, bondAmount?: number, currentStatus?: string}>}
 */
export const validateSetAside = async (powerNumber) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/set-aside/validate/${powerNumber}`);
    const json = await response.json().catch(() => ({}));

    if (!response.ok) {
      return {
        success: false,
        canSetAside: false,
        message: json.message || 'Failed to validate power',
      };
    }

    return json;
  } catch (error) {
    console.error('Validate set-aside error:', error);
    return { success: false, canSetAside: false, message: 'Network error' };
  }
};

/**
 * Execute set-aside (reinstatement) on a power.
 * Power must be in Forfeiture ("F") status and pass validation.
 * @param {string} powerNumber - Power number
 * @param {Object} data - Set-aside data
 * @param {string} data.reasonCode - Reason code from /reason-codes endpoint
 * @param {string} [data.otherReason] - Free-text reason (required when reasonCode is 'OT')
 * @returns {Promise<{success: boolean, message?: string, powerNumber?: string}>}
 */
export const setAsidePower = async (powerNumber, data) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/set-aside/${powerNumber}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    const json = await response.json().catch(() => ({}));

    if (!response.ok || !json.success) {
      return {
        success: false,
        message: json.message || 'Failed to set aside power',
      };
    }

    return json;
  } catch (error) {
    console.error('Set aside power error:', error);
    return { success: false, message: 'Network error' };
  }
};

// ============================================
// DISCHARGE OPERATIONS
// ============================================

/**
 * Discharge an individual power
 * @param {string} powerNo - Power number
 * @param {Object} data - Discharge data
 * @param {string} data.dischargeDate - Discharge date (ISO 8601)
 * @param {number} data.dischargeAmount - Discharge amount
 * @param {string[]} [data.proofDocumentIds] - Uploaded proof file IDs (when proof was required)
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const dischargePower = async (powerNo, data) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerinquiry/powers/${powerNo}/discharge`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return {
        success: false,
        error: result.message || 'Failed to discharge power',
        data: null,
      };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Discharge power error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

/**
 * Bulk discharge multiple powers
 * When selectAllMatching is true, sends searchCriteria so the backend can
 * apply the discharge to all matching records server-side.
 * @param {Object} data - Bulk discharge data
 * @param {string[]} [data.powerNumbers] - Array of power numbers (when not selectAllMatching)
 * @param {string} data.dischargeDate - Discharge date (ISO 8601)
 * @param {number} data.dischargeAmount - Discharge amount
 * @param {boolean} [data.selectAllMatching] - If true, discharge all matching search results
 * @param {Object} [data.searchCriteria] - Original search criteria (required when selectAllMatching)
 * @returns {Promise<{success: boolean, data?: {processedCount: number, failedCount: number, errors?: string[]}, error?: string}>}
 */
export const bulkDischargePowers = async (data) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerinquiry/powers/bulk-discharge`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return {
        success: false,
        error: result.message || 'Failed to bulk discharge powers',
        data: null,
      };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Bulk discharge powers error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};
